import React, { useMemo } from 'react';
import { useLocation, useMatches } from 'react-router-dom';
import { SlideLayout } from '../slide-layout/SlideLayout';
import { SlideAction, ViewMode } from '../slide-layout/enums';
import {
  ColumnData,
  SlideLayoutLocationState,
} from '../slide-layout/interfaces';

export type PanelMeta = { key: string; title: string; rawTitle?: string };
export type RouteHandle = { panel?: PanelMeta };

export type PanelShellProps = {
  leftColumn: ColumnData;
  /**
   * When set, this prop is forwarded to SlideLayout's `rightColumn` and takes
   * precedence over any column registered via `useSlidePanel` (e.g. the assets
   * selection panel while rows are selected).
   */
  rightColumnOverride?: ColumnData;
  /**
   * Additional fields merged into the outlet context alongside `SlidePanelRegistrar`.
   * Passed through to SlideLayout's `outletContext` prop.
   */
  outletContextExtras?: object;
  /**
   * Override the number of visible panel columns.
   * Defaults to 1 on compact (phone/small tablet) and 2 on wide screens.
   */
  visibleCount?: number;
};

/**
 * Generic section root that wraps `SlideLayout` and drives its `transitionKey`
 * from the active route's `handle.panel` declarations.
 *
 * P1: child route components still register their `ColumnData` via `useSlidePanel`
 * through the `<Outlet />` that `SlideLayout` renders when `enableChildRoutes` is
 * set. `PanelShell` uses `stack` today only for:
 *   - `transitionKey` — gives SlideLayout the active route stack depth
 *   - future dev warnings ("route declared handle.panel but didn't register column")
 *
 * P2 (follow-up PR): shell renders `handle.panel.Component` at stable React keys
 * and `useSlidePanel` is retired.
 */
export const PanelShell: React.FC<PanelShellProps> = ({
  leftColumn,
  rightColumnOverride,
  outletContextExtras,
}) => {
  const location = useLocation();
  const matches = useMatches();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  // Collect the panel metadata declared on every matched route in the active tree.
  const stack = useMemo(
    () =>
      matches
        .map((m) => (m.handle as RouteHandle | undefined)?.panel)
        .filter((p): p is PanelMeta => !!p),
    [matches]
  );

  // SlideLayout uses the key's stack depth to animate depth changes. Same-depth
  // route swaps refresh the active panel content in place.
  const transitionKey = stack.map((p) => p.key).join('>');
  const viewMode =
    stack.length > 0 || !!rightColumnOverride ? ViewMode.DUAL : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumnOverride}
      viewMode={viewMode}
      transitionKey={transitionKey}
      initialTransition={initialTransition}
      enableChildRoutes
      outletContext={outletContextExtras}
    />
  );
};
